import React, { useState, useEffect, useRef, useMemo } from 'react';
import { WallApp } from '../wall-components';

/**
 * Preload an image and return a promise
 */
const preloadImage = (url) => {
  return new Promise((resolve, reject) => {
    if (!url) {
      resolve(null);
      return;
    }
    const img = new Image();
    img.onload = () => resolve(url);
    img.onerror = () => reject(new Error(`Failed to load: ${url}`));
    img.src = url;
  });
};

/**
 * Preload a video's metadata and return a promise
 */
const preloadVideo = (url) => {
  return new Promise((resolve, reject) => {
    if (!url) {
      resolve(null);
      return;
    }
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => resolve(url);
    video.onerror = () => reject(new Error(`Failed to load video: ${url}`));
    video.src = url;
  });
};

/**
 * ReplayPlayer - cycles through posts and media pairs (background + matching partner logo)
 */
// Number of posts each partner stays visible before switching to next partner
const POSTS_PER_PARTNER = 3;

export const ReplayPlayer = ({ data }) => {
  const { posts, mediaPairs, config } = data;

  const [currentPostIndex, setCurrentPostIndex] = useState(0);
  const [imagesPreloaded, setImagesPreloaded] = useState(false);

  const postTimerRef = useRef(null);

  // Group mediaPairs by partner (partnerLogo URL)
  const partnerGroups = useMemo(() => {
    if (!mediaPairs || mediaPairs.length === 0) return [];

    const groups = new Map();
    mediaPairs.forEach((pair, index) => {
      // Use partnerLogo as the partner identifier, or 'no-logo' for pairs without logo
      const partnerId = pair.partnerLogo || 'no-logo';
      if (!groups.has(partnerId)) {
        groups.set(partnerId, []);
      }
      groups.get(partnerId).push({ ...pair, originalIndex: index });
    });

    // Convert to array of { partnerId, partnerLogo, backgrounds: [...] }
    return Array.from(groups.entries()).map(([partnerId, pairs]) => ({
      partnerId,
      partnerLogo: partnerId === 'no-logo' ? null : partnerId,
      backgrounds: pairs,
    }));
  }, [mediaPairs]);

  // Calculate current partner and background based on post count
  const { currentPartner, currentBackground } = useMemo(() => {
    if (partnerGroups.length === 0) {
      return { currentPartner: null, currentBackground: null };
    }

    // Which partner's turn is it? (changes every POSTS_PER_PARTNER posts)
    const partnerIndex = Math.floor(currentPostIndex / POSTS_PER_PARTNER) % partnerGroups.length;
    const partner = partnerGroups[partnerIndex];

    // Which background within this partner? (cycles within the partner's window)
    const postWithinPartner = currentPostIndex % POSTS_PER_PARTNER;
    const bgIndex = postWithinPartner % partner.backgrounds.length;
    const background = partner.backgrounds[bgIndex];

    return {
      currentPartner: partner,
      currentBackground: background,
    };
  }, [currentPostIndex, partnerGroups]);

  // Preload all images and videos on mount
  useEffect(() => {
    const imagesToPreload = [];
    const videosToPreload = [];

    // Collect snap images from posts
    posts?.forEach((post) => {
      if (post.imageUrl) {
        imagesToPreload.push(post.imageUrl);
      }
    });

    // Collect background images/videos and partner logos from mediaPairs
    mediaPairs?.forEach((pair) => {
      if (pair.background?.url) {
        if (pair.background.type === 'video') {
          videosToPreload.push(pair.background.url);
        } else {
          imagesToPreload.push(pair.background.url);
        }
      }
      if (pair.partnerLogo) {
        imagesToPreload.push(pair.partnerLogo);
      }
    });

    // Preload all images and videos
    const preloadTasks = [
      ...imagesToPreload.map(preloadImage),
      ...videosToPreload.map(preloadVideo),
    ];

    if (preloadTasks.length > 0) {
      Promise.allSettled(preloadTasks)
        .then(() => {
          console.log(`Preloaded ${imagesToPreload.length} images, ${videosToPreload.length} videos`);
          setImagesPreloaded(true);
        });
    } else {
      setImagesPreloaded(true);
    }
  }, [posts, mediaPairs]);

  // Cycle through posts
  useEffect(() => {
    if (!posts || posts.length === 0) return;

    const cyclePost = () => {
      setCurrentPostIndex((prev) => (prev + 1) % posts.length);
    };

    postTimerRef.current = setInterval(cyclePost, config.postDuration);

    return () => {
      if (postTimerRef.current) clearInterval(postTimerRef.current);
    };
  }, [posts, config.postDuration]);


  // Build wallData object for WallApp
  const currentPost = posts[currentPostIndex];
  // Use snap image as background if available, otherwise use current partner's background
  const currentBg = currentPost?.imageUrl
    ? { url: currentPost.imageUrl, type: 'image' }
    : currentBackground?.background;
  // Partner logo comes from the current partner group
  const currentLogo = currentPartner?.partnerLogo;

  const wallData = {
    isWallOnly: true,
    isLandscapeOnly: false,
    isReady: true,
    wallAspectRatio: 'w-dvw h-dvh',
    pipe: {
      isOnAir: true,
    },
    wallMessage: {
      postItem: currentPost
        ? {
            _id: currentPost._id,
            message: currentPost.message,
            userName: currentPost.userName,
            type: currentPost.type,
            postTypeForWall: currentPost.postTypeForWall || currentPost.type,
            emblemUrl: currentPost.emblemUrl,
            backgroundOnly: currentPost.backgroundOnly,
          }
        : null,
    },
    wallBg: {
      currentMedia: currentBg,
    },
    wallPartner: {
      partnerLogo: currentLogo,
    },
    wallSettings: {
      wallMessage: {
        messageFadeDuration: 500,
      },
      wallBg: {
        BGFadeDuration: 500,
      },
      wallCTA: {
        ctaShow: true,
      },
      dropShadowStyle: 'drop-shadow-[0_0_1rem_rgba(0,0,0,0.25)]',
    },
    urlnoProt: window.location.hostname === 'localhost' ? 'localhost:5173' : 'pipeup.live',
  };

  return (
    <div className="relative overflow-hidden group/templateWall templateWall w-dvw h-dvh">
      <div className="absolute h-dvh w-dvw">
        <div id="wall" className="w-full h-full">
          <WallApp wallData={wallData} showWallLogo={true} />
        </div>
      </div>
    </div>
  );
};

export default ReplayPlayer;
